import { DefaultUrlSerializer, Routes, Route } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../environments/environment';

// Guards
import { AuthGuard } from './core/guards/auth.guard';
import { InventoryAccessGuard } from './core/guards/inventory-access.guard';
import { RcGuard } from '@core/guards/rc.guard';
import { RcRedirectGuard } from '@core/guards/rc-redirect.guard';

// Components
import { LayoutsComponent } from './layouts/layouts.component';
import { AuthComponent } from './auth/auth.component';
import { RcComponent } from './select-rc/rc.component';
import { BlankComponent } from './layouts/blank/blank.component';
import { RedirectPageComponent } from './redirect-page/redirect-page.component';
import { NotFoundComponent } from '@app/not-found/not-found.component';
import { SeoGuard } from './core/guards/seo.guard';
import { POAccessGuard } from './core/guards/po-access.guard';

// Optimized preloading strategy for better performance
@Injectable({
	providedIn: 'root'
})
export class SelectivePreloadingStrategy {
	private readonly preloadedRoutes = new Set<string>();

	preload(route: Route, load: () => Observable<any>): Observable<any> {
		// Only preload critical routes that are accessed immediately
		const criticalRoutes = ['auth'];

		// Check if route should be preloaded
		const shouldPreload =
			route.path &&
			criticalRoutes.includes(route.path) &&
			!this.preloadedRoutes.has(route.path);

		if (shouldPreload && route.path) {
			this.preloadedRoutes.add(route.path);
			return load();
		}

		// For all other routes, use intersection observer to preload when visible
		if (route.path && !this.preloadedRoutes.has(route.path)) {
			this.preloadOnVisibility(route.path, load);
		}

		return of(null);
	}

	private preloadOnVisibility(
		routePath: string,
		load: () => Observable<any>
	): void {
		// Use intersection observer to preload routes when they become visible
		// This reduces initial bundle size while still providing good UX
		if ('IntersectionObserver' in window) {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (
							entry.isIntersecting &&
							!this.preloadedRoutes.has(routePath)
						) {
							this.preloadedRoutes.add(routePath);
							load().subscribe();
							observer.disconnect();
						}
					});
				},
				{ rootMargin: '50px' }
			);

			// Observe navigation elements that might trigger route changes
			const navElements = document.querySelectorAll(
				'a[href*="/' + routePath + '"]'
			);
			navElements.forEach((el) => observer.observe(el));
		}
	}
}

// Optimized URL Serializer with production redirect and select-rc logic (disabled for now)
export class CustomUrlSerializer extends DefaultUrlSerializer {
	override parse(url: string): any {
		// For localhost, use default parsing without any custom logic
		if (
			window.location.hostname.includes('localhost') ||
			window.location.hostname.includes('127.0.0.1') ||
			window.location.hostname === 'rms.katdev.com'
		) {
			return super.parse(url);
		}

		// Production redirect logic - for base URL and select-rc
		if (
			environment.production &&
			(window.location.hostname === 'rms2.precisioncsrms.com' ||
				window.location.hostname === 'beta.precisioncsrms.com') &&
			(url === '/' ||
				url === '' ||
				url === '/select-rc' ||
				url.startsWith('/select-rc/'))
		) {
			// Redirect to external URL
			window.location.href =
				'https://precisioncs.com/road-management-software/';
			return super.parse(url); // This won't be reached but needed for type safety
		}

		// Exclude specific paths from RC handling to prevent redirect loops
		const excludedPaths = ['/not-found', '/auth', '/inventoryloginbridges'];
		if (
			excludedPaths.some(
				(path) => url === path || url.startsWith(path + '/')
			)
		) {
			return super.parse(url);
		}

		// For all other cases, use default parsing to avoid redirect loops
		return super.parse(url);
	}

	override serialize(tree: any): string {
		return super.serialize(tree);
	}
}

export const routes: Routes = [
	{
		path: '',
		canActivate: [AuthGuard],
		component: LayoutsComponent,
		loadChildren: () =>
			import('./pages/pages.module').then((m) => m.PagesModule)
	},
	// {
	//     path: 'select-rc',
	//     component: RcComponent,
	//     loadChildren: () =>
	//         import('./select-rc/rc.module').then((m) => m.RcModule)
	// },
	{
		path: 'auth',
		component: AuthComponent,
		canActivate: [SeoGuard],
		loadChildren: () =>
			import('./auth/auth.routes').then((m) => m.AUTH_ROUTES)
	},
	{
		path: 'pto-requests',
		canActivate: [AuthGuard],
		component: LayoutsComponent,
		loadChildren: () => import('./pages/pto/pto.routes'),
		title: 'PTO Requests'
	},
	{ path: 'inventoryloginbridges', component: RedirectPageComponent },
	{
		path: 'inventory',
		canActivate: [InventoryAccessGuard],
		component: LayoutsComponent,
		loadChildren: () =>
			import('./inventory/inventory.module').then(
				(m) => m.InventoryModule
			),
		data: { feature: 'inventory' }
	},
	{
		path: 'barcode',
		component: BlankComponent,
		loadChildren: () =>
			import('./barcode/barcode.module').then((m) => m.BarCodeModule)
	},
	{
		path: 'purchase-orders',
		canActivate: [AuthGuard, POAccessGuard],
		component: LayoutsComponent,
		loadChildren: () => import('./purchase-orders/purchase-orders.routes'),
		title: 'Purchase Orders'
	},
	{ path: 'not-found', component: NotFoundComponent },
	{
		path: ':rc/select-rc',
		canActivate: [RcRedirectGuard],
		component: RcComponent,
		loadChildren: () =>
			import('./select-rc/rc.module').then((m) => m.RcModule)
	},
	{
		path: ':rc/auth',
		canActivate: [RcGuard],
		component: AuthComponent,
		loadChildren: () =>
			import('./auth/auth.routes').then((m) => m.AUTH_ROUTES)
	},
	{
		path: ':rc/pto-requests',
		canActivate: [AuthGuard, RcGuard],
		component: LayoutsComponent,
		loadChildren: () => import('./pages/pto/pto.routes'),
		title: 'PTO Requests'
	},
	{
		path: ':rc/purchase-orders',
		canActivate: [AuthGuard, RcGuard, POAccessGuard],
		component: LayoutsComponent,
		loadChildren: () => import('./purchase-orders/purchase-orders.routes'),
		title: 'Purchase Orders'
	},
	{
		path: ':rc/inventory',
		canActivate: [InventoryAccessGuard, RcGuard],
		component: LayoutsComponent,
		loadChildren: () =>
			import('./inventory/inventory.module').then(
				(m) => m.InventoryModule
			),
		data: { feature: 'inventory' },
		title: 'Inventory'
	},
	{
		path: ':rc',
		canActivate: [AuthGuard, RcGuard],
		component: LayoutsComponent,
		loadChildren: () =>
			import('./pages/pages.module').then((m) => m.PagesModule),
		title: 'Home'
	},
	{ path: '**', redirectTo: '/not-found' }
];
